import { useCallback, useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, CheckSquare, ChevronLeft, ChevronRight, Printer, Search } from "lucide-react";

const ITEMS_PER_PAGE = 20;

const paymentOptions = [
  { value: "all", label: "Semua Pembayaran" },
  { value: "unpaid", label: "Belum Bayar" },
  { value: "partially_paid", label: "Bayar Sebagian" },
  { value: "waiting_verification_dp", label: "Menunggu Verifikasi DP" },
  { value: "dp_paid", label: "Sudah DP" },
  { value: "waiting_verification_settlement", label: "Menunggu Verifikasi Pelunasan" },
  { value: "paid", label: "Lunas" },
  { value: "cancelled", label: "Dibatalkan" },
];

const orderOptions = [
  { value: "all", label: "Semua Status" },
  { value: "pending", label: "Pending" },
  { value: "waiting_po_close", label: "Menunggu PO Close" },
  { value: "po_closed", label: "PO Ditutup" },
  { value: "sent_to_central", label: "Dikirim ke Pusat" },
  { value: "in_production", label: "Dalam Produksi" },
  { value: "ready_at_central", label: "Ready di Pusat" },
  { value: "ready_at_gallery", label: "Ready di Gallery" },
  { value: "ready_to_ship", label: "Siap Kirim" },
  { value: "shipped", label: "Dikirim" },
  { value: "completed", label: "Selesai" },
  { value: "cancelled", label: "Dibatalkan" },
];

const valueOrDash = (value) => {
  const text = String(value ?? "").trim();
  return text || "-";
};

const escapeLabelHtml = (value) => String(valueOrDash(value))
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const chunkLabels = (labels, size = 4) => {
  const pages = [];
  for (let i = 0; i < labels.length; i += size) {
    pages.push(labels.slice(i, i + size));
  }
  return pages;
};

const buildPrintLabelCardHtml = (label) => {
  const items = (label.items || []).map((item) => `
    <div class="item-row">
      <div>${escapeLabelHtml(item.name)}</div>
      <div class="item-qty">x${Number(item.qty || 0)}</div>
    </div>
  `).join("");

  return `
    <article class="label-card">
      <section>
        <div class="recipient-name">${escapeLabelHtml(label.recipient?.name)} <span>(${escapeLabelHtml(label.recipient?.phone)})</span></div>
        <div class="address"><strong>Alamat:</strong><br />${escapeLabelHtml(label.recipient?.address)}</div>
      </section>
      <section>
        <strong>Pengirim:</strong><br />${escapeLabelHtml(label.sender?.name)} (${escapeLabelHtml(label.sender?.phone)})
      </section>
      <div class="divider"></div>
      <section class="shipment">
        <div>${escapeLabelHtml(label.shipment?.courier)} <span>(${escapeLabelHtml(label.shipment?.weight)})</span></div>
        <div class="shipping-cost">Ongkir: ${escapeLabelHtml(label.shipment?.shipping_cost)}</div>
      </section>
      <div class="divider"></div>
      <section><span class="tracking-badge">ID Transaksi: ${escapeLabelHtml(label.transaction?.id)}</span></section>
      <section class="items">${items}</section>
      <section class="footer">📦 Total Item ${Number(label.footer?.total_qty || 0)} pcs</section>
    </article>
  `;
};

const buildPrintLabelsDocument = (labels) => {
  const pagesHtml = chunkLabels(labels, 4).map((pageLabels) => `
    <div class="label-page">
      <div class="label-grid">
        ${pageLabels.map(buildPrintLabelCardHtml).join("")}
      </div>
    </div>
  `).join("");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Print Label Pengiriman</title>
  <style>
    @page { size: A4 portrait; margin: 25.4mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; color: #000; font-family: Arial, Helvetica, sans-serif; }
    .label-page { width: 100%; break-after: page; page-break-after: always; }
    .label-page:last-child { break-after: auto; page-break-after: auto; }
    .label-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, auto); gap: 3mm; width: 100%; align-items: start; }
    .label-card { width: 100%; border: 2px solid #000; border-radius: 8px; padding: 4mm; overflow: visible; break-inside: avoid; page-break-inside: avoid; font-size: 10pt; line-height: 1.25; }
    .label-card section { margin: 0 0 2.5mm; }
    .recipient-name { font-weight: 700; font-size: 11pt; }
    .recipient-name span { font-weight: 400; }
    .address { margin-top: 1mm; }
    .divider { border-top: 1px solid #000; margin: 0 0 2.5mm; }
    .shipment { font-size: 12pt; font-weight: 900; text-transform: uppercase; line-height: 1.08; }
    .shipping-cost { font-size: 10pt; font-weight: 700; text-transform: none; margin-top: 1mm; }
    .tracking-badge { display: inline-block; background: #000; color: #fff; border-radius: 999px; padding: 1mm 2mm; font-size: 9pt; font-weight: 700; }
    .items { margin-top: 1.5mm; }
    .item-row { display: grid; grid-template-columns: 1fr 12mm; gap: 2mm; margin-bottom: 1.2mm; overflow-wrap: anywhere; font-size: 8.5pt; line-height: 1.15; }
    .item-qty { text-align: right; font-weight: 700; white-space: nowrap; font-size: 8.5pt; }
    .footer { background: #f5f5f4; border-radius: 6px; padding: 1.5mm 2mm; font-weight: 700; }
  </style>
</head>
<body>${pagesHtml}</body>
</html>`;
};

const printLabelsInWindow = (labels) => {
  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (!printWindow) {
    toast.error("Popup print diblokir browser. Izinkan popup untuk halaman staging ini, lalu coba lagi.");
    return;
  }
  printWindow.document.open();
  printWindow.document.write(buildPrintLabelsDocument(labels));
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 300);
};

const badgeClassForStatus = (value) => {
  const key = String(value || "").toLowerCase();
  if (["paid", "completed", "ready_to_ship", "ready_at_gallery"].includes(key)) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (["waiting_verification_dp", "waiting_verification_settlement", "dp_paid", "partially_paid"].includes(key)) return "bg-blue-100 text-blue-800 border-blue-200";
  if (["cancelled"].includes(key)) return "bg-red-100 text-red-800 border-red-200";
  if (["po_closed", "sent_to_central", "in_production", "ready_at_central"].includes(key)) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-stone-100 text-stone-700 border-stone-200";
};

function ShippingLabelPrintPreview({ labels, onClose }) {
  const totalLabels = labels.length;
  const hasMissingData = labels.some((label) => (label.missing_fields || []).length > 0);
  const labelPages = useMemo(() => chunkLabels(labels, 4), [labels]);

  return (
    <div id="shipping-label-print-modal" className="fixed inset-0 z-[9999] bg-stone-100 overflow-auto">
      <style>{`
        @page { size: A4 portrait; margin: 25.4mm; }
        .label-sheet { box-sizing: border-box; }
        .label-page { box-sizing: border-box; break-after: page; page-break-after: always; }
        .label-page:last-child { break-after: auto; page-break-after: auto; }
        .label-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, auto); gap: 4mm; align-items: start; }
        .label-card { box-sizing: border-box; width: 100%; break-inside: avoid; page-break-inside: avoid; overflow: visible; }
        @media print {
          html, body { width: auto !important; height: auto !important; margin: 0 !important; padding: 0 !important; background: white !important; overflow: visible !important; }
          body * { visibility: hidden !important; }
          #shipping-label-print-modal, #shipping-label-print-modal *,
          #shipping-label-print-area, #shipping-label-print-area * { visibility: visible !important; }
          #shipping-label-print-modal { position: static !important; inset: auto !important; overflow: visible !important; background: white !important; width: 100% !important; height: auto !important; }
          #shipping-label-print-area { position: static !important; inset: auto !important; background: white; padding: 0 !important; margin: 0 !important; width: 100% !important; overflow: visible !important; }
          .no-print { display: none !important; }
          .label-sheet { padding: 0 !important; margin: 0 !important; box-shadow: none !important; width: 100% !important; min-height: auto !important; }
          .label-page { width: 100% !important; break-after: page; page-break-after: always; }
          .label-page:last-child { break-after: auto; page-break-after: auto; }
          .label-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, auto); gap: 3mm; width: 100% !important; align-items: start; }
          .label-card { width: 100% !important; max-width: none !important; break-inside: avoid; page-break-inside: avoid; overflow: visible; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white border-b border-stone-200 px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-stone-900">Preview Cetak Label Pengiriman</h2>
          <p className="text-sm text-stone-500">{totalLabels} label siap dicetak dalam layout A4 2 kolom.</p>
          {hasMissingData && (
            <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Beberapa label punya data kosong dan akan dicetak sebagai tanda “-”.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">Kembali</button>
          <button type="button" onClick={() => printLabelsInWindow(labels)} className="px-4 py-2 rounded-lg bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800 flex items-center gap-2"><Printer className="w-4 h-4" /> Print</button>
        </div>
      </div>

      <div id="shipping-label-print-area" className="p-4 md:p-6">
        <div className="label-sheet bg-white mx-auto shadow-sm" style={{ width: "210mm", minHeight: "297mm", padding: "25.4mm" }}>
          {labelPages.map((pageLabels, pageIdx) => (
            <div key={pageIdx} className="label-page mb-6 last:mb-0">
              <div className="label-grid">
                {pageLabels.map((label, idx) => (
                  <div key={`${label.id || label.transaction_id}-${pageIdx}-${idx}`} className="label-card border-2 border-black rounded-lg" style={{ padding: "4mm", margin: "0", breakInside: "avoid", pageBreakInside: "avoid" }}>
                    <div className="space-y-[2.5mm] text-[10pt] leading-snug text-black">
                      <section>
                        <div className="font-bold text-[11pt]">{valueOrDash(label.recipient?.name)} <span className="font-normal">({valueOrDash(label.recipient?.phone)})</span></div>
                        <div className="mt-[1mm]"><span className="font-bold">Alamat:</span><br />{valueOrDash(label.recipient?.address)}</div>
                      </section>

                      <section>
                        <span className="font-bold">Pengirim:</span><br />
                        {valueOrDash(label.sender?.name)} ({valueOrDash(label.sender?.phone)})
                      </section>

                      <div className="border-t border-black" />

                      <section className="font-black uppercase leading-tight" style={{ fontSize: "12pt" }}>
                        <div>{valueOrDash(label.shipment?.courier)} <span>({valueOrDash(label.shipment?.weight)})</span></div>
                        <div className="text-[10pt] font-bold normal-case mt-[1mm]">Ongkir: {valueOrDash(label.shipment?.shipping_cost)}</div>
                      </section>

                      <div className="border-t border-black" />

                      <section>
                        <span className="inline-flex bg-black text-white rounded-full px-2 py-0.5 text-[9pt] font-bold">ID Transaksi: {valueOrDash(label.transaction?.id)}</span>
                      </section>

                      <section className="space-y-[1.5mm]">
                        {(label.items || []).map((item, itemIdx) => (
                          <div key={itemIdx} className="grid gap-2 text-[8.5pt] leading-tight" style={{ gridTemplateColumns: "1fr 12mm" }}>
                            <div className="break-words">{valueOrDash(item.name)}</div>
                            <div className="text-right font-bold whitespace-nowrap">x{Number(item.qty || 0)}</div>
                          </div>
                        ))}
                      </section>

                      <section className="bg-stone-100 rounded-md px-2 py-1 font-bold">
                        📦 Total Item {Number(label.footer?.total_qty || 0)} pcs
                      </section>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminLabels() {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [orderFilter, setOrderFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [printLabels, setPrintLabels] = useState(null);
  const [printing, setPrinting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const pageIds = labels.map((row) => row.transaction_id).filter(Boolean);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id));

  const fetchLabels = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("limit", String(ITEMS_PER_PAGE));
    params.set("skip", String((page - 1) * ITEMS_PER_PAGE));
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (paymentFilter !== "all") params.set("payment_status", paymentFilter);
    if (orderFilter !== "all") params.set("order_status", orderFilter);

    API.get(`/admin/labels?${params.toString()}`)
      .then((res) => {
        setLabels(res.data.labels || []);
        setTotal(Number(res.data.total || 0));
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Gagal memuat daftar label");
      })
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, paymentFilter, orderFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handlePrint = async () => {
    if (!selectedIds.length) return;
    setPrinting(true);
    try {
      const res = await API.post("/admin/labels/preview", { transaction_ids: selectedIds });
      const payload = res.data.labels || [];
      if (res.data.has_missing_data) {
        toast.warning("Beberapa transaksi belum punya data label lengkap. Data kosong akan dicetak sebagai '-' .");
      }
      setPrintLabels(payload);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat preview label");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <AdminLayout title="Label Pengiriman">
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-stone-400 font-bold">Fulfillment</p>
            <h1 className="font-serif text-3xl text-stone-900 mt-1">Label Pengiriman</h1>
            <p className="text-sm text-stone-500 mt-2 max-w-2xl">Pilih beberapa transaksi, lalu cetak label pengiriman batch dalam layout A4 2 kolom. Data shipping yang kosong akan tampil sebagai tanda “-”.</p>
          </div>
          <button
            type="button"
            onClick={handlePrint}
            disabled={!selectedIds.length || printing}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-stone-900 text-white text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-800"
          >
            <Printer className="w-4 h-4" />
            {printing ? "Menyiapkan..." : `Cetak Label Pengiriman${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
          </button>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_220px_220px] gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari order ID / penerima / HP / produk..." className="pl-9 bg-white" />
            </div>
            <Select value={paymentFilter} onValueChange={(value) => { setPaymentFilter(value); setPage(1); }}>
              <SelectTrigger><SelectValue placeholder="Status pembayaran" /></SelectTrigger>
              <SelectContent>{paymentOptions.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={orderFilter} onValueChange={(value) => { setOrderFilter(value); setPage(1); }}>
              <SelectTrigger><SelectValue placeholder="Status order" /></SelectTrigger>
              <SelectContent>{orderOptions.map((opt) => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between text-sm text-stone-500">
            <div className="flex items-center gap-2"><CheckSquare className="w-4 h-4" /> {selectedIds.length} transaksi dipilih</div>
            <div>Menampilkan {labels.length ? (page - 1) * ITEMS_PER_PAGE + 1 : 0}-{Math.min(page * ITEMS_PER_PAGE, total)} dari {total}</div>
          </div>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-xs uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-4 py-3 text-left w-12"><input type="checkbox" checked={allPageSelected} onChange={togglePage} /></th>
                  <th className="px-4 py-3 text-left">Order ID</th>
                  <th className="px-4 py-3 text-left">Penerima</th>
                  <th className="px-4 py-3 text-left min-w-[260px]">Alamat</th>
                  <th className="px-4 py-3 text-left">Kurir</th>
                  <th className="px-4 py-3 text-left">Berat</th>
                  <th className="px-4 py-3 text-left">Item</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Label</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading ? (
                  <tr><td colSpan="9" className="px-4 py-10 text-center text-stone-400">Memuat daftar label...</td></tr>
                ) : labels.length === 0 ? (
                  <tr><td colSpan="9" className="px-4 py-10 text-center text-stone-400">Belum ada transaksi yang cocok.</td></tr>
                ) : labels.map((row) => {
                  const hasMissing = (row.missing_fields || []).length > 0;
                  return (
                    <tr key={row.transaction_id} className="hover:bg-stone-50/70">
                      <td className="px-4 py-3"><input type="checkbox" checked={selectedSet.has(row.transaction_id)} onChange={() => toggleOne(row.transaction_id)} /></td>
                      <td className="px-4 py-3 font-mono text-xs font-bold text-stone-900 whitespace-nowrap">{row.transaction_id}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-stone-900">{valueOrDash(row.recipient_name)}</div>
                        <div className="text-xs text-stone-500">{valueOrDash(row.recipient_phone)}</div>
                      </td>
                      <td className="px-4 py-3 text-stone-600 max-w-sm"><div className="line-clamp-2">{valueOrDash(row.recipient_address)}</div></td>
                      <td className="px-4 py-3 font-semibold text-stone-800 whitespace-nowrap">{valueOrDash(row.courier)}</td>
                      <td className="px-4 py-3 text-stone-600 whitespace-nowrap">{valueOrDash(row.weight)}</td>
                      <td className="px-4 py-3 text-stone-700 whitespace-nowrap">{Number(row.total_item || 0)} pcs</td>
                      <td className="px-4 py-3 space-y-1 min-w-[170px]">
                        <Badge className={badgeClassForStatus(row.payment_status)}>{valueOrDash(row.payment_status_label)}</Badge>
                        <Badge className={badgeClassForStatus(row.order_status)}>{valueOrDash(row.order_status_label)}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        {hasMissing ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200"><AlertTriangle className="w-3 h-3 mr-1" /> Data kurang</Badge>
                        ) : (
                          <Badge className="bg-stone-100 text-stone-700 border-stone-200">{row.label_status || "Belum dicetak"}</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 bg-white">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-stone-200 text-sm disabled:opacity-50"><ChevronLeft className="w-4 h-4" /> Prev</button>
            <div className="text-sm text-stone-500">Halaman {page} dari {totalPages}</div>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-stone-200 text-sm disabled:opacity-50">Next <ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {printLabels && <ShippingLabelPrintPreview labels={printLabels} onClose={() => setPrintLabels(null)} />}
    </AdminLayout>
  );
}
